import React, { useMemo, useState } from "react";
import {
    Alert,
    Button,
    Card,
    DatePicker,
    Dropdown,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
    theme,
} from "antd";
import {
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    ImportOutlined,
    PlusOutlined,
    SearchOutlined,
    UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/es/table";
import { useIntl } from "react-intl";
import { useScopedNavigate } from "../components/MarketerView/useScopedNavigate";
import { useAuth } from "../hooks/useAuth";
import {
    useCreateCustomerMutation,
    useDeleteCustomerMutation,
    useGetActiveSegmentationFieldsQuery,
    useGetCustomersQuery,
    useLazyExportCustomersQuery,
    useUpdateCustomerMutation,
} from "../services/nestjs-base-api";
import {
    ICreateCustomerDto,
    ICustomer,
    ISegmentationField,
    SegmentationFieldType,
} from "../services/nestjs-base-api";

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const PAGE_SIZE = 25;

// Encode a value to the canonical storage form expected by the API.
const encodeValueForStorage = (
    field: ISegmentationField,
    raw: unknown,
): string | null => {
    if (raw === undefined || raw === null || raw === "") return null;
    switch (field.fieldType) {
        case "boolean":
            return raw ? "1" : "0";
        case "date":
            if (typeof raw === "string") return raw;
            if (dayjs.isDayjs(raw)) return raw.format("YYYY-MM-DD");
            return String(raw);
        case "multi_select":
            if (Array.isArray(raw)) return JSON.stringify(raw);
            return JSON.stringify([String(raw)]);
        case "currency": {
            const code = field.fieldMetadata?.currencyCode || "USD";
            const num = Number(raw);
            if (!Number.isFinite(num)) return null;
            return `${code}:${num}`;
        }
        case "number":
            return String(raw);
        case "text":
        case "select":
        default:
            return String(raw);
    }
};

// Decode the stored form back into an editor-shaped value.
const decodeValueForForm = (
    field: ISegmentationField,
    stored: { value: string | null; valueText: string | null } | undefined,
): unknown => {
    if (!stored) return undefined;
    switch (field.fieldType) {
        case "boolean":
            return stored.value === "1";
        case "date":
            return stored.value ? dayjs(stored.value) : undefined;
        case "multi_select": {
            if (!stored.valueText) return [];
            try {
                const parsed = JSON.parse(stored.valueText);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }
        case "currency": {
            if (!stored.value) return undefined;
            const colon = stored.value.indexOf(":");
            return colon === -1 ? Number(stored.value) : Number(stored.value.slice(colon + 1));
        }
        case "number":
            return stored.value !== null ? Number(stored.value) : undefined;
        default:
            return stored.value ?? undefined;
    }
};

const fieldTypeColors: Record<SegmentationFieldType, string> = {
    text: "blue",
    select: "green",
    number: "purple",
    date: "gold",
    boolean: "cyan",
    multi_select: "magenta",
    currency: "orange",
};

const renderStoredValue = (
    field: ISegmentationField,
    value?: { value: string | null; valueText: string | null },
): React.ReactNode => {
    if (!value) return <Text type="secondary">—</Text>;
    switch (field.fieldType) {
        case "boolean":
            return value.value === "1" ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>;
        case "multi_select": {
            try {
                const arr = value.valueText ? JSON.parse(value.valueText) : [];
                return (
                    <Space size={4} wrap>
                        {(arr as string[]).map((v) => (
                            <Tag key={v}>{v}</Tag>
                        ))}
                    </Space>
                );
            } catch {
                return <Text type="secondary">—</Text>;
            }
        }
        case "currency": {
            if (!value.value) return <Text type="secondary">—</Text>;
            const colon = value.value.indexOf(":");
            const code = colon === -1 ? "USD" : value.value.slice(0, colon);
            const num = colon === -1 ? value.value : value.value.slice(colon + 1);
            return <Text>{num} {code}</Text>;
        }
        default:
            return <Text>{value.value ?? "—"}</Text>;
    }
};

interface ValueEditorProps {
    field: ISegmentationField;
    // AntD Form.Item injects value/onChange (or checked/onChange for booleans)
    // into the direct child. We must spread them through, otherwise the control
    // is unwired from the form and never captures user input.
    [key: string]: unknown;
}

const ValueEditor: React.FC<ValueEditorProps> = ({ field, ...rest }) => {
    switch (field.fieldType) {
        case "boolean":
            return <Switch checkedChildren="Yes" unCheckedChildren="No" {...rest} />;
        case "date":
            return <DatePicker style={{ width: "100%" }} {...rest} />;
        case "select":
            return (
                <Select allowClear {...rest}>
                    {(field.options ?? []).map((opt) => (
                        <Option key={opt} value={opt}>{opt}</Option>
                    ))}
                </Select>
            );
        case "multi_select":
            return (
                <Select mode="multiple" allowClear {...rest}>
                    {(field.options ?? []).map((opt) => (
                        <Option key={opt} value={opt}>{opt}</Option>
                    ))}
                </Select>
            );
        case "number":
            return <InputNumber style={{ width: "100%" }} {...rest} />;
        case "currency": {
            const code = field.fieldMetadata?.currencyCode ?? "USD";
            return (
                <InputNumber
                    style={{ width: "100%" }}
                    addonAfter={code}
                    min={0}
                    step={0.01}
                    {...rest}
                />
            );
        }
        case "text":
        default:
            return <Input {...rest} />;
    }
};

interface CustomerEditorModalProps {
    open: boolean;
    initial: ICustomer | null;
    fields: ISegmentationField[];
    saving: boolean;
    onClose: () => void;
    onSubmit: (payload: ICreateCustomerDto, id: number | null) => Promise<void>;
}

const CustomerEditorModal: React.FC<CustomerEditorModalProps> = ({
    open,
    initial,
    fields,
    saving,
    onClose,
    onSubmit,
}) => {
    const intl = useIntl();
    const [form] = Form.useForm();

    React.useEffect(() => {
        if (!open) return;
        if (initial) {
            const segByField = new Map(
                (initial.segmentationValues ?? []).map((sv) => [sv.fieldId, sv]),
            );
            const values: Record<string, unknown> = {
                externalId: initial.externalId ?? undefined,
                email: initial.email ?? undefined,
                firstName: initial.firstName ?? undefined,
                lastName: initial.lastName ?? undefined,
                company: initial.company ?? undefined,
            };
            for (const f of fields) {
                values[`field_${f.id}`] = decodeValueForForm(f, segByField.get(f.id));
            }
            form.setFieldsValue(values);
        } else {
            form.resetFields();
        }
    }, [open, initial, fields, form]);

    const handleFinish = async (values: Record<string, unknown>) => {
        const segmentationValues: Record<string, string> = {};
        for (const f of fields) {
            const raw = values[`field_${f.id}`];
            const encoded = encodeValueForStorage(f, raw);
            if (encoded !== null) {
                segmentationValues[f.fieldName] = encoded;
            }
        }
        const payload: ICreateCustomerDto = {
            externalId: (values.externalId as string) || undefined,
            email: (values.email as string) || undefined,
            firstName: (values.firstName as string) || undefined,
            lastName: (values.lastName as string) || undefined,
            company: (values.company as string) || undefined,
            segmentationValues,
        };
        await onSubmit(payload, initial?.id ?? null);
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={640}
            title={
                initial
                    ? intl.formatMessage({ id: "customers.editor.editTitle", defaultMessage: "Edit customer" })
                    : intl.formatMessage({ id: "customers.editor.newTitle", defaultMessage: "New customer" })
            }
        >
            <Form form={form} layout="vertical" onFinish={handleFinish}>
                <Form.Item
                    name="email"
                    label={intl.formatMessage({ id: "customers.editor.email", defaultMessage: "Email" })}
                    rules={[{ type: "email", message: intl.formatMessage({ id: "customers.editor.emailInvalid", defaultMessage: "Enter a valid email" }) }]}
                >
                    <Input placeholder="customer@example.com" />
                </Form.Item>
                <Form.Item
                    name="externalId"
                    label={intl.formatMessage({ id: "customers.editor.externalId", defaultMessage: "External ID" })}
                    tooltip={intl.formatMessage({ id: "customers.editor.externalIdHint", defaultMessage: "Identifier from your CRM, Shopify, etc. Used to match survey responses." })}
                >
                    <Input placeholder="e.g. shopify:1234" />
                </Form.Item>
                <Space size="large" style={{ width: "100%" }}>
                    <Form.Item
                        name="firstName"
                        label={intl.formatMessage({ id: "customers.editor.firstName", defaultMessage: "First name" })}
                        style={{ flex: 1 }}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="lastName"
                        label={intl.formatMessage({ id: "customers.editor.lastName", defaultMessage: "Last name" })}
                        style={{ flex: 1 }}
                    >
                        <Input />
                    </Form.Item>
                </Space>
                <Form.Item
                    name="company"
                    label={intl.formatMessage({ id: "customers.editor.company", defaultMessage: "Company" })}
                >
                    <Input />
                </Form.Item>

                {fields.length > 0 && (
                    <>
                        <Title level={5} style={{ marginTop: 24, marginBottom: 12 }}>
                            {intl.formatMessage({ id: "customers.editor.segmentationTitle", defaultMessage: "Segmentation values" })}
                        </Title>
                        {fields.map((f) => (
                            <Form.Item
                                key={f.id}
                                name={`field_${f.id}`}
                                label={
                                    <Space>
                                        <span>{f.fieldLabel}</span>
                                        <Tag color={fieldTypeColors[f.fieldType]}>{f.fieldType}</Tag>
                                        {f.fieldMetadata?.isPii && <Tag color="red">PII</Tag>}
                                    </Space>
                                }
                                valuePropName={f.fieldType === "boolean" ? "checked" : "value"}
                            >
                                <ValueEditor field={f} />
                            </Form.Item>
                        ))}
                    </>
                )}

                <Form.Item style={{ textAlign: "right", marginBottom: 0 }}>
                    <Space>
                        <Button onClick={onClose}>
                            {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
                        </Button>
                        <Button type="primary" htmlType="submit" loading={saving}>
                            {initial
                                ? intl.formatMessage({ id: "common.save", defaultMessage: "Save" })
                                : intl.formatMessage({ id: "common.create", defaultMessage: "Create" })}
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

const CustomersPage: React.FunctionComponent = () => {
    const intl = useIntl();
    const navigate = useScopedNavigate();
    const { user } = useAuth();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState<ICustomer | null>(null);

    const jwtToken = user?.jwtToken ?? "";
    const offset = (page - 1) * PAGE_SIZE;

    const [triggerExport, exportResult] = useLazyExportCustomersQuery();

    const handleExport = async (format: "csv" | "xlsx") => {
        if (!jwtToken) return;
        try {
            const { blob, filename } = await triggerExport({ jwtToken, format }).unwrap();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err: any) {
            message.error(err?.data?.message || err?.message || "Export failed");
        }
    };

    const { data: customersResponse, isLoading: customersLoading, refetch } = useGetCustomersQuery(
        { jwtToken, search: search || undefined, limit: PAGE_SIZE, offset },
        { skip: !jwtToken },
    );

    const { data: fieldsData, isLoading: fieldsLoading } = useGetActiveSegmentationFieldsQuery(
        { jwtToken },
        { skip: !jwtToken },
    );

    const fields = useMemo<ISegmentationField[]>(() => fieldsData ?? [], [fieldsData]);

    const [createCustomer, { isLoading: creating }] = useCreateCustomerMutation();
    const [updateCustomer, { isLoading: updating }] = useUpdateCustomerMutation();
    const [deleteCustomer] = useDeleteCustomerMutation();

    const handleSubmit = async (payload: ICreateCustomerDto, id: number | null) => {
        try {
            if (id) {
                await updateCustomer({ jwtToken, customerId: id, payload }).unwrap();
                message.success(intl.formatMessage({ id: "customers.toast.updated", defaultMessage: "Customer updated" }));
            } else {
                await createCustomer({ jwtToken, payload }).unwrap();
                message.success(intl.formatMessage({ id: "customers.toast.created", defaultMessage: "Customer created" }));
            }
            setEditorOpen(false);
            setEditing(null);
            refetch();
        } catch (err: any) {
            message.error(err?.data?.message || intl.formatMessage({ id: "customers.toast.saveFailed", defaultMessage: "Save failed" }));
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await deleteCustomer({ jwtToken, customerId: id }).unwrap();
            message.success(intl.formatMessage({ id: "customers.toast.deleted", defaultMessage: "Customer deleted" }));
            refetch();
        } catch (err: any) {
            message.error(err?.data?.message || intl.formatMessage({ id: "customers.toast.deleteFailed", defaultMessage: "Delete failed" }));
        }
    };

    const columns: ColumnsType<ICustomer> = useMemo(() => {
        const base: ColumnsType<ICustomer> = [
            {
                title: intl.formatMessage({ id: "customers.columns.name", defaultMessage: "Name" }),
                key: "name",
                render: (_, c) => {
                    const name = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
                    return name || <Text type="secondary">—</Text>;
                },
            },
            {
                title: intl.formatMessage({ id: "customers.columns.email", defaultMessage: "Email" }),
                dataIndex: "email",
                key: "email",
                render: (e) => e || <Text type="secondary">—</Text>,
            },
            {
                title: intl.formatMessage({ id: "customers.columns.externalId", defaultMessage: "External ID" }),
                dataIndex: "externalId",
                key: "externalId",
                render: (e) => e || <Text type="secondary">—</Text>,
            },
            {
                title: intl.formatMessage({ id: "customers.columns.company", defaultMessage: "Company" }),
                dataIndex: "company",
                key: "company",
                render: (e) => e || <Text type="secondary">—</Text>,
            },
        ];
        const fieldCols: ColumnsType<ICustomer> = fields.map((f) => ({
            title: (
                <Tooltip title={f.fieldName}>
                    <Space size={4}>
                        <span>{f.fieldLabel}</span>
                        {f.fieldMetadata?.isPii && <Tag color="red">PII</Tag>}
                    </Space>
                </Tooltip>
            ),
            key: `field_${f.id}`,
            render: (_, c) => {
                const v = c.segmentationValues?.find((sv) => sv.fieldId === f.id);
                return renderStoredValue(f, v);
            },
        }));
        const actions: ColumnsType<ICustomer> = [
            {
                title: intl.formatMessage({ id: "customers.columns.actions", defaultMessage: "Actions" }),
                key: "actions",
                fixed: "right",
                render: (_, c) => (
                    <Space>
                        <Button
                            icon={<EditOutlined />}
                            size="small"
                            onClick={() => {
                                setEditing(c);
                                setEditorOpen(true);
                            }}
                        />
                        <Popconfirm
                            title={intl.formatMessage({ id: "customers.confirmDelete.title", defaultMessage: "Delete customer?" })}
                            description={intl.formatMessage({ id: "customers.confirmDelete.body", defaultMessage: "This removes the customer record. Linked survey responses keep their frozen snapshots." })}
                            onConfirm={() => handleDelete(c.id)}
                            okText={intl.formatMessage({ id: "common.delete", defaultMessage: "Delete" })}
                            okButtonProps={{ danger: true }}
                            cancelText={intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
                        >
                            <Button icon={<DeleteOutlined />} size="small" danger />
                        </Popconfirm>
                    </Space>
                ),
            },
        ];
        return [...base, ...fieldCols, ...actions];
    }, [fields, intl]);

    return (
        <>
            <Card
                style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}
                title={
                    <Space>
                        <UserOutlined />
                        <Title level={4} style={{ margin: 0 }}>
                            {intl.formatMessage({ id: "customers.title", defaultMessage: "Customers" })}
                        </Title>
                    </Space>
                }
                extra={
                    <Space>
                        <Search
                            allowClear
                            placeholder={intl.formatMessage({ id: "customers.searchPlaceholder", defaultMessage: "Search by name, email, external ID" })}
                            prefix={<SearchOutlined />}
                            onSearch={(val) => {
                                setPage(1);
                                setSearch(val);
                            }}
                            style={{ width: 320 }}
                        />
                        <Dropdown
                            disabled={exportResult.isFetching}
                            menu={{
                                items: [
                                    {
                                        key: "csv",
                                        label: intl.formatMessage({ id: "customers.export.csv", defaultMessage: "Export as CSV" }),
                                        onClick: () => handleExport("csv"),
                                    },
                                    {
                                        key: "xlsx",
                                        label: intl.formatMessage({ id: "customers.export.xlsx", defaultMessage: "Export as Excel" }),
                                        onClick: () => handleExport("xlsx"),
                                    },
                                ],
                            }}
                        >
                            <Button icon={<DownloadOutlined />} loading={exportResult.isFetching}>
                                {intl.formatMessage({ id: "customers.exportCta", defaultMessage: "Export" })}
                            </Button>
                        </Dropdown>
                        <Button
                            icon={<ImportOutlined />}
                            onClick={() => navigate("/dashboard/customers/import")}
                        >
                            {intl.formatMessage({ id: "customers.importCta", defaultMessage: "Import" })}
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setEditing(null);
                                setEditorOpen(true);
                            }}
                        >
                            {intl.formatMessage({ id: "customers.newCta", defaultMessage: "New customer" })}
                        </Button>
                    </Space>
                }
            >
                {fields.length === 0 && !fieldsLoading && (
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={intl.formatMessage({ id: "customers.noFieldsYet.title", defaultMessage: "No segmentation fields yet" })}
                        description={intl.formatMessage({ id: "customers.noFieldsYet.body", defaultMessage: "Create fields under the Fields tab to start segmenting customers (e.g. tier, country, total_spend)." })}
                    />
                )}

                <Table<ICustomer>
                    rowKey="id"
                    loading={customersLoading || fieldsLoading}
                    columns={columns}
                    dataSource={customersResponse?.customers ?? []}
                    scroll={{ x: "max-content" }}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={intl.formatMessage({ id: "customers.empty", defaultMessage: "No customers yet" })}
                            />
                        ),
                    }}
                    pagination={{
                        current: page,
                        pageSize: PAGE_SIZE,
                        total: customersResponse?.total ?? 0,
                        onChange: setPage,
                        showTotal: (total) =>
                            intl.formatMessage(
                                { id: "customers.pagination.total", defaultMessage: "Total {total} customer(s)" },
                                { total },
                            ),
                    }}
                />
            </Card>

            <CustomerEditorModal
                open={editorOpen}
                initial={editing}
                fields={fields}
                saving={creating || updating}
                onClose={() => {
                    setEditorOpen(false);
                    setEditing(null);
                }}
                onSubmit={handleSubmit}
            />
        </>
    );
};

export default CustomersPage;
